import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const navigate = useNavigate();
  const text1 = 'Your Partner in Digital Transformation';
  const text2 = 'Simplifying Business Solutions';

  const charVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.1 } },
  };

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32"
      style={{
        background: `linear-gradient(to bottom, rgba(14, 58, 99, 0.8), rgba(14, 58, 99, 0.8)), url(/assest/logo.jfif)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Background Gradient Orb - Using inline styles for reliability */}
      <div 
        className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] md:w-[1200px] md:h-[800px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 0%, rgba(239, 205, 134, 0.15) 0%, rgba(4, 106, 140, 0.1) 40%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      
      {/* Secondary glow layer */}
      <div 
        className="absolute top-[5%] left-1/2 -translate-x-1/2 w-[600px] h-[400px] md:w-[800px] md:h-[600px] pointer-events-none animate-pulse"
        style={{
          background: 'radial-gradient(circle at 50% 0%, rgba(239, 205, 134, 0.2) 0%, rgba(4, 106, 140, 0.15) 50%, transparent 70%)',
          filter: 'blur(40px)',
          animationDuration: '4s',
        }}
      />

      {/* Large glow effect */}
      <div 
        className="absolute inset-0 scale-[2] opacity-60 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(239, 205, 134, 0.6) 0%, rgba(4, 106, 140, 0.4) 40%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      {/* Large outlined numbers */}
      
      

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6">
            <motion.span 
              className="block text-white mb-2"
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { staggerChildren: 0.04 } }
              }}
            >
              {text1.split('').map((char, index) => (
                <motion.span key={`t1-${index}`} variants={charVariants}>
                  {char}
                </motion.span>
              ))}
            </motion.span>
            
            <motion.span 
              className="block text-[#EFCD86]"
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { delayChildren: text1.length * 0.04 + 0.3, staggerChildren: 0.04 } }
              }}
            >
              {text2.split('').map((char, index) => (
                <motion.span key={`t2-${index}`} variants={charVariants}>
                  {char}
                </motion.span>
              ))}
            </motion.span>
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed"
        >
          MakSoft is a fast-growing software development house founded in April 1996. We design professional Business Solutions presented with friendly, easy-to-use interfaces.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button className="group px-8 py-4 rounded-lg bg-[#046A8C] text-white font-semibold text-lg hover:bg-[#0588B3] transition-all duration-300 shadow-lg flex items-center gap-2">
            Partner With Us
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
          
          <button 
            onClick={() => navigate('/solutions')}
            className="group px-8 py-4 rounded-lg bg-[#0d1f33] border border-[#EFCD86]/30 text-[#EFCD86] font-semibold text-lg hover:bg-[#EFCD86]/10 hover:border-[#EFCD86] transition-all duration-300 flex items-center gap-2"
          >
            <Play size={20} style={{ fill: '#EFCD86' }} />
            Our Solutions
          </button>
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 flex items-center justify-center gap-2 text-sm text-gray-500"
        >
          <div className="flex -space-x-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-8 h-8 rounded-full bg-gradient-to-br from-[#EFCD86] to-[#046A8C] border-2 border-[#0a1628]"
              />
            ))}
          </div>
          <span className="ml-3">
            Trusted by <span className="text-[#EFCD86] font-semibold">500+</span> businesses
          </span>
        </motion.div>
      </div>

      {/* Bottom gradient fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-48"
        style={{ background: 'linear-gradient(to top, #0e3a63, transparent)' }}
      />
    </section>
  );
};

export default Hero;
